import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loader2, Printer, X } from "lucide-react";
import { imageUrl, labNotes, sheetFabrics } from "@/lib/modelSheets";

const today = () => new Date().toLocaleDateString("it-IT");

const TechHead = ({ s }) => (
  <header className="mb-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b-2 border-black pb-2" data-testid="print-tech-head">
    <h1 className="text-2xl font-bold uppercase">{s.name}{s.code && <span className="ml-3 font-semibold">{s.code}</span>}</h1>
    <p className="flex gap-5 text-sm"><span><span className="text-gray-500">Taglia base </span><b>{s.base_size || "-"}</b></span><span><span className="text-gray-500">Data </span><b>{today()}</b></span></p>
  </header>
);

const Box = ({ title, children, className = "mb-5" }) => (
  <section className={`break-inside-avoid ${className}`}>
    <h2 className="mb-1.5 border-b border-gray-400 text-xs font-bold uppercase tracking-wider">{title}</h2>{children}
  </section>
);

const Grid = ({ head, rows }) => (
  <table className="w-full border-collapse text-xs">
    <thead><tr>{head.map((h, i) => <th key={i} className="border border-gray-400 bg-gray-300 px-2 py-1 text-left font-semibold">{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} className={i % 2 ? "bg-gray-200" : "bg-white"}>{r.map((c, j) => <td key={j} className="border border-gray-400 px-2 py-1">{c}</td>)}</tr>)}</tbody>
  </table>
);

const TechPage = ({ s }) => {
  const img = imageUrl(s);
  return (
    <div className="sheet-page" data-testid="print-tech">
      <TechHead s={s} />
      <div className="mb-4 grid grid-cols-[1fr_1fr] gap-5 break-inside-avoid">
        <div className="flex h-60 items-center justify-center border border-gray-300">
          {img ? <img src={img} alt={s.name} className="max-h-full max-w-full object-contain" data-testid="print-image" /> : <span className="text-xs text-gray-400">Nessuna immagine</span>}
        </div>
        <section className="break-inside-avoid" data-testid="print-lab-notes">
          <h2 className="mb-1.5 bg-black px-2 py-1 text-xs font-bold uppercase tracking-wider text-white">Note per il laboratorio</h2>
          <p className="min-h-[200px] whitespace-pre-wrap border-2 border-black p-2 text-sm">{labNotes(s) || " "}</p>
        </section>
      </div>
      {s.attention?.trim() && (
        <section className="mb-5 break-inside-avoid border-[3px] border-red-600 p-3" data-testid="print-attention">
          <h2 className="mb-1 text-base font-black uppercase tracking-wider text-red-600">⚠ Attenzione</h2>
          <p className="whitespace-pre-wrap text-base font-bold">{s.attention}</p>
        </section>
      )}
      <Box title="Tabella misure (cm)">
        <Grid head={["Punto di misura", ...s.sizes.map((z) => (z === s.base_size ? `${z} (base)` : z))]} rows={(s.tech?.measures || []).map((m) => [m.point, ...s.sizes.map((z) => m.values?.[z] || "")])} />
      </Box>
      <div className="grid grid-cols-2 gap-5">
        <Box title="Tessuti" className="">
          <Grid head={["Tipo di tessuto"]} rows={sheetFabrics(s).map((f) => [f.fabric_type || f.name || "-"])} />
        </Box>
        <Box title="Accessori e materiali" className="">
          <Grid head={["Accessorio", "Quantità"]} rows={(s.tech?.accessories || []).map((a) => [a.name, a.quantity])} />
        </Box>
      </div>
    </div>
  );
};

export default function SchedaStampa() {
  const { id } = useParams();
  const { data: s, isLoading, isError } = useQuery({ queryKey: ["model-sheet", id], queryFn: async () => (await api.get(`/model-sheets/${id}`)).data });
  useEffect(() => { if (s) { document.title = `${s.name} - Scheda tecnica`; } }, [s]);
  if (isLoading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (isError || !s) return <p className="p-10 text-center text-sm">Scheda non trovata.</p>;
  return (
    <div className="print-root min-h-screen bg-gray-200 py-6 text-black" data-testid="sheet-print-page">
      <div className="no-print mx-auto mb-4 flex max-w-[210mm] justify-end gap-2">
        <button onClick={() => window.close()} className="flex items-center gap-1 rounded border border-gray-400 bg-white px-3 py-1.5 text-sm" data-testid="print-close"><X className="h-4 w-4" />Chiudi</button>
        <button onClick={() => window.print()} className="flex items-center gap-1 rounded bg-black px-3 py-1.5 text-sm text-white" data-testid="print-btn"><Printer className="h-4 w-4" />Stampa / Salva PDF</button>
      </div>
      <TechPage s={s} />
    </div>
  );
}
